import { createIdempotencyKey, platform } from '@/services/platform'

// 常量：订单套餐 SKU 与页面中文名映射
const PLAN_NAME_MAP = {
  web: '网页端套餐',
  mini: '小程序套餐',
  both: '双端套餐'
}

// 常量：订单周期与页面中文名映射
const PERIOD_NAME_MAP = {
  month: '月付',
  quarter: '季付',
  year: '年付'
}

// 常量：支付渠道与页面中文名映射
const PAYMENT_METHOD_MAP = {
  wechat: '微信支付',
  alipay: '支付宝支付',
  mock: '测试支付'
}

// 方法：兼容数组响应和分页响应，避免列表接口结构调整时页面直接报错
function normalizeList(response) {
  if (Array.isArray(response)) {
    return response
  }

  if (response && Array.isArray(response.items)) {
    return response.items
  }

  return []
}

// 方法：把金额分值格式化为人民币展示文本
function formatAmountText(amountCents) {
  const cents = Number(amountCents) || 0
  return `¥${(cents / 100).toFixed(2)}`
}

// 方法：把平台时间格式化为页面展示文本
function formatDateTime(value) {
  const date = new Date(value)
  if (!value || Number.isNaN(date.getTime())) {
    return '--'
  }
  return date.toLocaleString('zh-CN', { hour12: false })
}

// 方法：把订单接口返回值适配成发票申请页订单模型
function mapInvoiceableOrder(order) {
  const planName = PLAN_NAME_MAP[order.plan_sku] || order.plan_sku || '--'
  const periodName = PERIOD_NAME_MAP[order.period] || order.period || '--'
  const amountCents = Number(order.amount_cents) || 0

  return {
    id: order.id,
    orderNo: order.order_number || order.id || '--',
    planName: `${planName}（${periodName}）`,
    packageName: `${planName}（${periodName}）`,
    paidAt: formatDateTime(order.paid_at),
    paymentMethod: PAYMENT_METHOD_MAP[order.payment_provider] || '测试支付',
    amount: amountCents / 100,
    amountCents,
    amountText: formatAmountText(amountCents),
    status: '已支付',
    raw: order
  }
}

// 方法：判断订单是否可用于申请发票
function isInvoiceableOrder(order) {
  const amountCents = Number(order && order.amount_cents) || 0

  // 逻辑分支：文档没有独立可开票订单接口，前端按已支付且金额大于 0 过滤
  return Boolean(
    order &&
    order.id &&
    order.payment_status === 'SUCCEEDED' &&
    amountCents > 0
  )
}

// 方法：获取可开票订单，按文档复用订单列表接口并过滤已支付订单
export async function getInvoiceableOrders() {
  const orders = normalizeList(await platform.orders())
  return orders.filter(isInvoiceableOrder).map(mapInvoiceableOrder)
}

// 方法：提交发票申请，使用 Idempotency-Key 避免重复创建申请
export function submitInvoiceApplication(payload) {
  return platform.createInvoice(
    payload,
    createIdempotencyKey('invoice')
  )
}

// 方法：查询当前用户发票记录
export async function getInvoiceRecords() {
  return normalizeList(await platform.invoices())
}
